import * as Sentry from '@sentry/browser';

type SiteL2Type = Record<string, string>;

type PlayerType = 'smallplayer' | 'bigplayer' | 'bigplayer_play';

const videoAdRequest = () => {
  const cncPageDefinition = window.__cncPageDefinition || {};

  // supported ad sizes
  const adSizes = ['1920x1080', '1280x720', '1024x576', '768x576', '640x480', '640x360', ''];
  // url advertising system
  const baseUrl = 'https://pubads.g.doubleclick.net/gampad/ads';

  const adUnitKey = '/21869710533';
  const adUnitArea = 'ostatni';

  const siteL2: SiteL2Type = {
    abicko: 'cnc260',
    ahaonline: 'cnc260',
    auto: 'cnc260',
    autorevue: 'cnc260',
    blesk: 'cnc260',
    bleskprozeny: 'cnc260',
    dama: 'cnc260',
    e15: 'cnc260',
    eleague: 'cnr260',
    evropa2: 'cnr260',
    extra: 'cnr260',
    extralife: 'cnr260',
    extrastory: 'cnr260',
    frekvence1: 'cnr260',
    gcz: 'cnr260',
    hledejceny: 'cnc260',
    hokej: 'cnr260',
    info: 'cnr260',
    isport: 'cnc260',
    lideazeme: 'cnc260',
    lifee: 'cnr260',
    malltv: 'cnr260',
    maminka: 'cnc260',
    mimibazarcz: 'cnc260',
    mimibazarsk: 'cnc260',
    mobilmania: 'cnc260',
    mojezdravi: 'cnc260',
    mojepsychologie: 'cnc260',
    nhl: 'cnr260',
    onetv: 'cnr260',
    recepty: 'cnc260',
    reflex: 'cnc260',
    sparta: 'cnr260',
    sportrevue: 'cnc260',
    toprecepty: 'cnr260',
    youradio: 'cnr260',
    zeny: 'cnc260',
    zive: 'cnc260',
  };

  const getValuesPageDefinition = (key: string): string => {
    if (!(key in cncPageDefinition)) {
      return '';
    }

    const value = cncPageDefinition[key];
    if (typeof value === 'string') {
      return value;
    }

    return value.toString();
  };

  const getUnit = (playerType?: PlayerType) => {
    playerType = playerType === 'bigplayer_play' ? 'bigplayer' : (playerType ?? 'smallplayer');
    let site = getValuesPageDefinition('site');

    if (site) {
      site = site.replace(/dev_/g, '');
      const l2 = siteL2[site] || '';

      return `${adUnitKey}/${l2}/${site}/{position}_{count}/${playerType}/${adUnitArea}`;
    }

    return '';
  };

  const getQueryParams = (playerType?: PlayerType): string => {
    playerType = playerType === 'bigplayer_play' ? 'bigplayer' : (playerType ?? 'smallplayer');
    // query params do url
    const queryParams = {
      env: 'vp',
      gdfp_req: '1',
      impl: 's',
      iu: getUnit(playerType),
      npa: '0',
      output: 'vast',
      sz: encodeURIComponent(adSizes.join('|')),
      tfcd: '0',
      unviewed_position_start: '1',
      plcmt: '2',
    };

    const arr = Object.entries(queryParams).map((entry) => {
      return `${entry[0]}=${entry[1]}`;
    });

    return arr.join('&');
  };

  const getBisko = (): string => {
    try {
      const cxSegments = (localStorage.cxSegments || '') as string;
      const result = cxSegments;

      return result;
    } catch (e) {
      Sentry.captureException(e);
      return '';
    }
  };

  const getUrlTargeting = () => {
    return window.location.pathname.split('/').toString();
  };

  /**
   * Zkontrolujeme jestli jde o Seznam traffic přes SeznamNewsFeed cookie
   * @returns {boolean}
   */
  const getSeznamTrafficStatus = (): boolean => {
    try {
      const cookies = document.cookie.split(';');
      const seznamCookie = cookies.find((cookie) => cookie.trim().startsWith('SeznamNewsFeed='));

      if (seznamCookie) {
        const cookieValue = seznamCookie.split('=')[1]?.trim();
        return cookieValue === 'true';
      }

      return false;
    } catch (e) {
      Sentry.captureException(e);
      return false;
    }
  };

  const getCustomParams = (playerType?: PlayerType): string => {
    playerType = playerType === 'bigplayer_play' ? 'bigplayer' : (playerType ?? 'smallplayer');
    const site = getValuesPageDefinition('site').replace(/dev_/g, '');

    const customParams: Record<string, string | null> = {
      L2: siteL2[site] || null,
      URL: getUrlTargeting(),
      area: 'ostatni',
      bisko: getBisko(),
      keywords: getValuesPageDefinition('keywords'),
      safety: getValuesPageDefinition('safety'),
      site,
      template: playerType,
      target_position: '{position}_{count}',
    };

    // Přidá parametry pokud jde o Seznam traffic
    if (getSeznamTrafficStatus()) {
      customParams.show_vignette = 'true';
      customParams.seznam_traffic = 'true';
    }

    const customParamsArray = Object.entries(customParams).map((entry) => {
      return `${entry[0]}=${entry[1] as string}`;
    });

    let result = `cust_params=${encodeURIComponent(customParamsArray.join('&'))}`;

    // Zachováme nezakódované { a } pro {position}_{count} placeholder
    const reg1 = /(%7B)/g;
    const reg2 = /(%7D)/g;

    result = result.replace(reg1, '{');
    result = result.replace(reg2, '}');

    return result;
  };

  /**
   * URL pro miniplayer
   * @returns {string}
   */
  const getSmallPlayerTagUrl = (): string => {
    return `${baseUrl}?${getQueryParams('smallplayer')}&${getCustomParams('smallplayer')}`;
  };

  /**
   * URL pro velký přehrávač
   * @returns {string}
   */
  const getBigPlayerTagUrl = (): string => {
    return `${baseUrl}?${getQueryParams('bigplayer')}&${getCustomParams('bigplayer')}`;
  };

  return {
    getSmallPlayerTagUrl,
    getBigPlayerTagUrl,

    getBisko,
    getUrlTargeting,
    getValuesPageDefinition,
    getUnit,
    getCustomParams,
    getQueryParams,
    getSeznamTrafficStatus,
  };
};

export default videoAdRequest;

export interface VideoAdRequestType {
  getSmallPlayerTagUrl: () => string;
  getBigPlayerTagUrl: () => string;
  getBisko: () => string;
  getUrlTargeting: () => string;
  getValuesPageDefinition: (key: string) => string;
  getUnit: (playerType?: PlayerType) => string;
  getCustomParams: (playerType?: PlayerType) => string;
  getQueryParams: (playerType?: PlayerType) => string;
  getSeznamTrafficStatus: () => boolean;
}
